import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faBell, 
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import '../styles/NotificationBanner.css';

const NotificationBanner = () => {
  const { user } = useAuth(); // Add authentication check
  const { unreadCount, notifications } = useNotifications();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  // Show banner when there are unread notifications and it hasn't been dismissed
  useEffect(() => {
    if (unreadCount > 0 && !isDismissed) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [unreadCount, isDismissed]);

  // Reset dismissed state when all notifications are read
  useEffect(() => {
    if (unreadCount === 0) {
      setIsDismissed(false);
    }
  }, [unreadCount]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  const handleViewNotifications = () => {
    // You can implement this to open the notification dropdown
    // or navigate to a notifications page
    setIsDismissed(true);
    setIsVisible(false);
    
    // Trigger notification dropdown opening (you might need to pass this as a prop)
    const notificationButton = document.querySelector('.notification-bell');
    if (notificationButton) {
      notificationButton.click();
    }
  };

  const getLatestNotification = () => {
    return notifications.find(n => !n.is_read) || notifications[0];
  };

  const getBannerType = () => {
    const latestNotification = getLatestNotification();
    if (!latestNotification) return 'info';
    
    if (latestNotification.type === 'error') return 'urgent';
    if (latestNotification.type === 'warning') return 'warning';
    return 'info';
  };

  const getBannerIcon = () => {
    const type = getBannerType();
    switch (type) {
      case 'urgent':
        return faExclamationTriangle;
      case 'warning':
        return faExclamationTriangle;
      default:
        return faBell;
    }
  };

  // Don't show banner if user is not authenticated
  if (!user || !isVisible) return null;

  const latestNotification = getLatestNotification();
  const bannerType = getBannerType();

  return (
    <div className={`notification-banner notification-banner-${bannerType}`}>
      <div className="notification-banner-content">
        <div className="notification-banner-icon">
          <FontAwesomeIcon icon={getBannerIcon()} />
        </div>
        <div className="notification-banner-text">
          <div className="notification-banner-title">
            {unreadCount === 1 
              ? 'New notification' 
              : `${unreadCount} new notifications`
            }
          </div>
          {latestNotification && (
            <div className="notification-banner-message">
              {latestNotification.title}
              {unreadCount > 1 && ` and ${unreadCount - 1} more...`}
            </div>
          )}
        </div>
        <div className="notification-banner-actions">
          <button
            className="notification-banner-action-btn view-btn"
            onClick={handleViewNotifications}
            title="View notifications"
          >
            View
          </button>
          <button
            className="notification-banner-action-btn dismiss-btn"
            onClick={handleDismiss}
            title="Dismiss banner"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>
      <div className="notification-banner-pulse"></div>
    </div>
  );
};

export default NotificationBanner;
